import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon.js';

/**
 * Exactly one of two to four short options in one row of equal segments (UX line 19.10.2026, "Segmente"): presets,
 * stop mode, taste, period, clock in standby, switch type, language. Never wraps. With `allowNone` a tap on the chosen
 * segment takes it back (taste). The segments are toggle buttons (aria-pressed); test IDs testId-<value>.
 */
export function Segmented<T extends string | number>({
  label,
  options,
  onPick,
  disabled = false,
  allowNone = false,
  testId,
  itemTestId = testId,
  compact = false,
  className,
}: {
  label: string;
  options: readonly { value: T; label: ReactNode; on: boolean; hint?: string }[];
  onPick: (value: T) => void;
  disabled?: boolean;
  allowNone?: boolean;
  testId?: string;
  /** prefix of the segments' test IDs when it differs from the group's (chips / chip-1) */
  itemTestId?: string | undefined;
  /** as wide as its labels (the rating next to its question), not the whole row */
  compact?: boolean;
  className?: string;
}) {
  return (
    <div
      className={['segmented', compact ? 'is-compact' : '', className ?? ''].filter(Boolean).join(' ')}
      role="group"
      aria-label={label}
      data-testid={testId}
    >
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          className="segment"
          aria-pressed={o.on}
          disabled={disabled}
          title={o.hint}
          onClick={() => (allowNone || !o.on) && onPick(o.value)}
          data-testid={itemTestId ? `${itemTestId}-${o.value}` : undefined}
        >
          <span className="segment-label">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

export interface NavItem {
  key: string;
  label: ReactNode;
  /** the summary under the label ("Mo–Fr 06:45 · Sa, So 08:30") */
  hint?: ReactNode;
  /** an icon in front (the pages of Einstellungen, the rows of Konto) */
  icon?: IconName;
  /** the value at the right, before the chevron ("30 min", "Deutsch") */
  value?: ReactNode;
  onClick: () => void;
  testId?: string;
}

/**
 * Rows that open a sub-page (UX line "Navigation = Zeile mit Pfeil"): the whole row is the button, the label left with
 * its summary below, the chevron right.
 */
export function NavList({ items, testId }: { items: readonly NavItem[]; testId?: string }) {
  return (
    <ul className="list nav-list" data-testid={testId}>
      {items.map((i) => (
        <li key={i.key} className="nav-item">
          <button type="button" className="nav-row" onClick={i.onClick} data-testid={i.testId}>
            {i.icon && <Icon name={i.icon} className="nav-icon" />}
            <span className="row-text">
              <span className="row-label">{i.label}</span>
              {i.hint && <span className="hint">{i.hint}</span>}
            </span>
            {i.value !== undefined && <span className="row-aside">{i.value}</span>}
            <Icon name="chevronRight" size="sm" className="list-chevron" />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * Key figures as columns, the name above its value (recipe summary, counters; 20.10.2026: name first, as one reads
 * it). `rows`: the figures fill rows of three; a null item keeps its place empty, so the columns line up.
 */
export function FactRow({
  items,
  rows = false,
  testId,
}: {
  items: readonly ({ key: string; label: string; value: ReactNode; testId?: string } | null)[];
  rows?: boolean;
  testId?: string;
}) {
  return (
    <dl className={rows ? 'fact-row is-rows' : 'fact-row'} data-testid={testId}>
      {items.map((x, i) =>
        x ? (
          <div key={x.key} className="fact">
            <dt>{x.label}</dt>
            <dd className="num" data-testid={x.testId}>
              {x.value}
            </dd>
          </div>
        ) : (
          <div key={`empty-${i}`} className="fact" aria-hidden="true" />
        ),
      )}
    </dl>
  );
}

/**
 * Progress as a native <progress> (UX line: soft, fully rounded track and fill, 8 px; due turns it orange). The label
 * says what it measures ("Tropfschale: 420 / 600 ml").
 */
export function ProgressBar({
  fraction,
  due = false,
  label,
}: {
  fraction: number;
  due?: boolean;
  label?: string;
}) {
  const value = Math.round(Math.max(0, Math.min(1, fraction)) * 1000);
  return (
    <progress
      className={due ? 'bar bar-due' : 'bar'}
      max={1000}
      value={value}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

/**
 * Rating a shot (Geschmack): the question at the left, a small segmented control at the right (approved 20.10.2026: the
 * rating does not pull the eye away from the figures).
 */
export function RateRow<T extends string | number>({
  label,
  options,
  onPick,
  disabled,
  testId,
}: {
  label: string;
  options: readonly { value: T; label: ReactNode; on: boolean; hint?: string }[];
  onPick: (value: T) => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <div className="rate">
      <span className="rate-label" aria-hidden="true">
        {label}
      </span>
      <Segmented
        label={label}
        options={options}
        onPick={onPick}
        disabled={disabled}
        allowNone
        compact
        testId={testId}
      />
    </div>
  );
}
